// One Piece TCG Types for Master Set tracking and card data

export interface OnePieceSet {
  id: string; // e.g., "OP01", "ST01"
  name: string;
  series: string; // e.g., "Booster", "Starter Deck"
  printedTotal: number;
  totalCards: number; // Total cards including secret rares
  releaseDate: string;
  images: {
    symbol?: string;
    logo: string;
  };
  era: string; // e.g., "Early", "Mid", "Recent"
  valueTier: string; // e.g., "Premium Value", "High Value", "Medium Value", "Standard"
  owned: number;
  completion: number;
  description?: string; // Set description/lore
  msrp?: number; // MSRP in USD
  marketPrice?: number; // Current market price for sealed box
  sealedItems?: OnePieceSealedProduct[];
}

export interface OnePieceSealedProduct {
  id: string;
  name: string;
  type: string; // "Booster Box", "Starter Deck", "Extra Booster", etc.
  setId: string;
  releaseDate: string;
  imageUrl: string;
  msrp?: number;
  marketPrice?: number;
}

export interface OnePieceCard {
  id: string; // Card ID from API, e.g., "OP01-001"
  cardId: string; // Same as id for consistency
  baseCardId?: string; // The base card set ID (e.g. OP01-001) vs the specific image ID
  setId: string; // e.g., "OP01"
  name: string;
  nameJp?: string; // Japanese name if available
  justtcgName?: string; // Name from JustTCG API
  justtcgId?: string; // ID from JustTCG API
  setName?: string; // Descriptive set name (e.g. "Romance Dawn")
  number: string; // Card number in set
  rarity: string; // e.g., "C", "UC", "R", "SR", "SEC", "L", "SP"
  cost?: number; // Cost to play (for non-leader cards)
  attribute?: string; // e.g., "Strike", "Slash", "Special", etc.
  power?: number; // Card power
  counter?: number; // Counter value
  color: string[]; // e.g., ["Red"], ["Blue", "Green"] for multicolor
  type: string[]; // e.g., ["Character", "Straw Hat Crew"]
  effect?: string; // Card effect text
  trigger?: string; // Trigger effect if any
  category?: string; // Card category: "Leader", "Character", "Event", "Stage"
  images: {
    small?: string;
    large: string;
    smallJp?: string;
    largeJp?: string;
  };
  prices?: {
    low?: number;
    mid?: number;
    high?: number;
    market?: number;
  };
  isChaseCard?: boolean; // Computed based on rarity
}

export interface OnePieceSetProgress {
  setId: string;
  owned: number;
  total: number;
  completion: number;
  missingCards: string[];
  lastUpdated: string;
}

export interface OnePieceBulkAddRequest {
  setId: string;
  cards: {
    cardId: string;
    quantity: number;
    condition: string;
    purchasePrice: number;
  }[];
  totalCost: number;
  purchaseDate: string;
  source: string;
}

// Chase card criteria - based on rarity
// Chase card criteria - based on rarity
export const CHASE_RARITIES = [
  'SR', // Secret Rare
  'SEC', // Secret
  'SP', // Special
  'SP CARD', // Special Card
  'MANGA', // Manga Rare
  'ALT', // Alternate Art
  'PARALLEL', // Parallel
  'TR', // Treasure Rare
];

export function isChaseCard(card: OnePieceCard): boolean {
  const rarityUpper = card.rarity?.toUpperCase().trim() || '';
  const isRarityChase = CHASE_RARITIES.some(chaseRarity => rarityUpper === chaseRarity);
  
  const nameUpper = card.name?.toUpperCase() || '';
  const isNameChase = 
      nameUpper.includes('(SP)') || 
      nameUpper.includes('ALTERNATE ART') || 
      nameUpper.includes('ALT ART');
      
  return isRarityChase || isNameChase;
}

// Color definitions for One Piece TCG
export enum OnePieceColor {
  Red = 'Red',
  Blue = 'Blue',
  Green = 'Green',
  Purple = 'Purple',
  Black = 'Black',
  Yellow = 'Yellow',
}

// Card categories
export enum OnePieceCategory {
  Leader = 'Leader',
  Character = 'Character',
  Event = 'Event',
  Stage = 'Stage',
}
